import { useNavigate, useParams } from "react-router-dom";
import { SearchX } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DeleteConfirmButton } from "@/components/delete-confirm-button";
import { DetailList, DetailRow } from "@/components/detail-list";
import { EntityButton } from "@/components/entity-button";
import { EmptyState } from "@/components/empty-state";
import { DetailSkeleton } from "@/components/loading-skeletons";
import { PageHeader, PageTitle } from "@/components/layout/page-header";
import { useDataSourceQuery, useDataSourceSnapshotQuery, useDeleteDataSourceMutation } from "@/hooks/use-data-sources";
import { formatDateTime } from "@/lib/utils";

const BREADCRUMBS = [
  { label: "Widgets", to: "/widgets" },
  { label: "Data Sources", to: "/widgets/data-sources" },
];

export function DataSourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: dataSource, isLoading, error, refetch } = useDataSourceQuery(id!);
  const customerUserId = (dataSource?.definition?.user_id as string) || "";
  const { data: snapshotData } = useDataSourceSnapshotQuery(id!, customerUserId);
  const deleteMutation = useDeleteDataSourceMutation();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id!);
      navigate("/widgets/data-sources");
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Data Source" />
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !dataSource) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Data Source" />
        <EmptyState
          icon={SearchX}
          title="Data Source Not Found"
          description="The data source you are looking for does not exist."
        >
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={dataSource.name}
        actions={
          <>
            <EntityButton entityName="Data Source" mode="edit" to={`/widgets/data-sources/${id}/edit`} />
            <DeleteConfirmButton
              entityName="Data Source"
              name={dataSource.name}
              onConfirm={handleDelete}
              mutation={deleteMutation}
            />
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <PageTitle title={dataSource.name} />

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              {dataSource.definition?.url ? (
                <DetailRow label="URL">{dataSource.definition.url as string}</DetailRow>
              ) : null}
              <DetailRow label="Created">{formatDateTime(dataSource.created_at)}</DetailRow>
              <DetailRow label="Updated">{formatDateTime(dataSource.updated_at)}</DetailRow>
            </DetailList>
          </CardContent>
        </Card>

        {dataSource.definition?.headers ? (
          <Card>
            <CardHeader>
              <CardTitle>Headers</CardTitle>
            </CardHeader>
            <CardContent>
              <DetailList>
                {Object.entries(
                  dataSource.definition.headers as Record<string, string>
                ).map(([key, value]) => (
                  <DetailRow key={key} label={key}>
                    {value}
                  </DetailRow>
                ))}
              </DetailList>
            </CardContent>
          </Card>
        ) : null}

        {snapshotData ? (
          <Card>
            <CardHeader>
              <CardTitle>Snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="max-h-[600px] overflow-auto rounded-lg bg-muted/60 p-4 font-mono text-xs leading-relaxed">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(snapshotData.snapshot), null, 2);
                  } catch {
                    return snapshotData.snapshot;
                  }
                })()}
              </pre>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
